/**
 * Utility functions for CopyableAmount component
 */

import { formatWu, WU_LABEL } from '../../../config/workUnits';

// Re-export so amount surfaces keep a single import site for formatting.
export { formatWu };

/**
 * Get currency label using explicit Unicode code points
 * This bypasses Windows font substitution that causes Thai character rendering
 * @param currency - The currency to get label for ('wu', 'olv' or 'usd')
 * @returns Unicode-safe string for the currency label
 *
 * 2026-08-13: 'wu' added for the PRO Work Units migration — display surfaces
 * moved from the legacy 'pr0' label to WU. 'olv' remains only for legacy
 * call sites pending removal.
 */
export function getCurrencyLabel(currency: 'wu' | 'olv' | 'usd'): string {
  if (currency === 'wu') {
    return WU_LABEL;
  }
  if (currency === 'olv') {
    // p=112, r=114, 0=48
    return String.fromCharCode(112, 114, 48);
  }
  // u=117, s=115, d=100
  return String.fromCharCode(117, 115, 100);
}

/**
 * Format OLV value with appropriate precision
 * Shows more decimal places for better transparency
 * @param value - The OLV value to format
 * @param showPrefix - Whether to show +/- prefix
 * @returns Formatted OLV string
 */
export function formatOlv(value: number, showPrefix = false): string {
  const prefix = showPrefix && value > 0 ? '+' : value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs === 0) return '0';

  // Very small amounts: show up to 8 decimal places
  if (abs < 0.01) return prefix + abs.toFixed(8).replace(/\.?0+$/, '');

  // Small amounts: show up to 6 decimal places
  if (abs < 1) return prefix + abs.toFixed(6).replace(/\.?0+$/, '');

  // Medium amounts: show up to 4 decimal places
  if (abs < 100) return prefix + abs.toFixed(4).replace(/\.?0+$/, '');

  // Large amounts: show up to 4 decimal places with locale formatting
  return prefix + abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/**
 * Format USD value as standard money: always exactly two decimals at or
 * above one cent (locale-grouped for thousands), with extra precision ONLY
 * below $0.01 where two decimals would collapse a real value to zero (e.g.
 * per-unit rates).
 *
 * 2026-08-14: previously this stripped trailing zeros and allowed up to four
 * decimals, which produced "$0.3" transaction fees and "-$99.882" wallet
 * balances in production. Money surfaces must read like money.
 *
 * @param value - The USD value to format
 * @param showPrefix - Whether to show +/- prefix
 * @returns Formatted USD string
 */
export function formatUsd(value: number, showPrefix = false): string {
  const prefix = showPrefix && value > 0 ? '+' : value < 0 ? '-' : '';
  const abs = Math.abs(value);

  if (abs === 0) return '$0.00';

  // Sub-cent amounts (per-unit rates): keep enough precision to be nonzero.
  if (abs < 0.01) return prefix + '$' + abs.toFixed(8).replace(/0+$/, '');

  // Everything else is money: exactly two decimals, grouped.
  return (
    prefix +
    '$' +
    abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
}

/**
 * Debounce function for hover events
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
